import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { GraduationCap, Buildings, User, ArrowsClockwise, Printer } from "@phosphor-icons/react";

export default function IssuedCertificatesPanel() {
  const [data, setData] = useState({ total: 0, issued: [] });
  const [log, setLog] = useState({ total: 0, recent: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all"); // all | user | token

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/issued-certificates");
      setData(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
    try {
      const { data: lg } = await api.get("/admin/certificates-log");
      setLog(lg);
    } catch (_) { /* scoped roles may 403 */ }
  };
  useEffect(() => { load(); }, []);

  const list = data.issued.filter(c => filter === "all" || c.kind === filter);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold font-display flex items-center gap-2">
          <GraduationCap size={22} weight="duotone" color="#CCFF00" />
          Issued certificates ({data.total})
        </h2>
        <div className="flex gap-1.5">
          {[
            { id: "all", label: "All" },
            { id: "user", label: "Students" },
            { id: "token", label: "School tokens" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              data-testid={`cert-filter-${t.id}`}
              className="px-3 py-1.5 rounded-full text-xs font-display font-semibold whitespace-nowrap transition-all"
              style={{
                background: filter === t.id ? "#CCFF00" : "#DCDEE2",
                color: filter === t.id ? "#DCDEE2" : "#52525B",
                border: "1px solid " + (filter === t.id ? "#CCFF00" : "#C4C8CE"),
                cursor: "pointer",
              }}
            >{t.label}</button>
          ))}
          <button className="btn-ghost p-1.5" onClick={load} title="Refresh"><ArrowsClockwise size={16} /></button>
        </div>
      </div>

      {err && <div className="text-[#FF5100] mb-3 text-sm">{err}</div>}
      {loading && <div className="text-[#000000]">Loading...</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#000000] uppercase text-xs tracking-[0.1em]">
              <th className="p-2">Recipient</th>
              <th className="p-2">Type</th>
              <th className="p-2">Course</th>
              <th className="p-2">Cert ID</th>
            </tr>
          </thead>
          <tbody>
            {list.map(c => (
              <motion.tr
                key={`${c.recipient_id}-${c.course_id}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="border-t border-[#C4C8CE]"
                data-testid={`issued-cert-${c.cert_id}`}
              >
                <td className="p-2">
                  <div className="font-semibold flex items-center gap-1.5">
                    {c.kind === "user" ? <User size={14} color="#52525B" /> : <Buildings size={14} color="#52525B" />}
                    {c.recipient_name}
                  </div>
                  {c.recipient_email && <div className="text-xs text-[#000000]">{c.recipient_email}</div>}
                  {c.school_name && <div className="text-xs text-[#000000]">{c.school_name}</div>}
                </td>
                <td className="p-2">
                  <span className="chip" style={{
                    background: c.kind === "user" ? "rgba(0,229,255,0.13)" : "rgba(204,255,0,0.13)",
                    color: c.kind === "user" ? "#7AE9FF" : "#DCFF6A",
                    border: "1px solid " + (c.kind === "user" ? "rgba(0,229,255,0.3)" : "rgba(204,255,0,0.3)"),
                  }}>{c.kind === "user" ? "Student" : "School Token"}</span>
                </td>
                <td className="p-2">{c.course_title}</td>
                <td className="p-2 font-mono text-xs text-[#CCFF00]">{c.cert_id}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && !loading && (
          <div className="text-[#000000] text-sm p-6 text-center">No certificates issued in this scope yet.</div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-[#C4C8CE]" data-testid="cert-audit-section">
        <h3 className="text-base font-bold font-display flex items-center gap-2 mb-2">
          <Printer size={18} weight="duotone" color="#CCFF00" /> Prints & downloads ({log.total})
        </h3>
        {log.recent.length === 0 ? (
          <div className="text-[#000000] text-sm p-3">No certificate prints or downloads yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#000000] uppercase text-xs tracking-[0.1em]">
                  <th className="p-2">When</th>
                  <th className="p-2">Certificate of</th>
                  <th className="p-2">Format</th>
                  <th className="p-2">By</th>
                  <th className="p-2">Course</th>
                </tr>
              </thead>
              <tbody>
                {log.recent.map((e, i) => (
                  <tr key={i} className="border-t border-[#E4E4E7]" data-testid={`cert-event-${i}`}>
                    <td className="p-2 whitespace-nowrap text-xs text-[#000000]">{new Date(e.at).toLocaleString()}</td>
                    <td className="p-2 font-semibold">{e.recipient_name}</td>
                    <td className="p-2">
                      <span className="chip text-xs" style={{ background: e.variant === "wallet" ? "#FEF3C7" : "#DCFCE7", color: e.variant === "wallet" ? "#92400E" : "#166534" }}>
                        {e.variant === "wallet" ? "Wallet card" : "Wall certificate"}
                      </span>
                    </td>
                    <td className="p-2 text-xs">
                      {e.actor_name}
                      <span className="text-[#000000]"> · {e.source === "self" ? "own download" : e.source === "roster" ? "class roster" : "admin panel"}</span>
                    </td>
                    <td className="p-2 text-xs">{e.course_title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
